import { useMemo } from "react";
import { useUserState } from "./useUserState";

const NOTHING_REMEMBERED = { selectedId: "", cleared: false };

export interface RememberedSelection<TOption> {
  selected: TOption | null;
  setSelected: (next: TOption | null) => void;
  /** Whether the stored selection has loaded, so a null `selected` means "nothing selected". */
  isReady: boolean;
}

/**
 * Remembers which of `options` the user picked, in the user's `userState` store.
 *
 * The selection is derived, never copied: a remembered id applies even when it arrives before
 * the options, the held option is re-resolved from each refreshed list, and falling back to the
 * first option is not written as if the user had picked it. An explicit clear stays cleared.
 *
 * @param key - Names the selection, e.g. `selectedPreset:faultMonitor`. Must be stable.
 * @param options - The options to choose from, in display order.
 */
export function useRememberedSelection<TOption extends { id: string }>(
  key: string,
  options: TOption[],
): RememberedSelection<TOption> {
  const { value, setValue, isReady } = useUserState(key, NOTHING_REMEMBERED);

  const selected = useMemo(() => {
    // Before the stored value loads, options[0] would be a wrong guess that triggers a fetch.
    if (!isReady || options.length === 0 || value.cleared) {
      return null;
    }
    return options.find(option => option.id === value.selectedId) ?? options[0];
  }, [isReady, options, value.cleared, value.selectedId]);

  const setSelected = (next: TOption | null) => {
    setValue({ selectedId: next?.id ?? "", cleared: next === null });
  };

  return { selected, setSelected, isReady };
}
